import * as React from "react";
import { cn } from "../../lib/cn";

/**
 * Composer.Root — the warm-cream card shell.
 *
 * Owns focus-within elevation (a deeper shadow lift, no coloured ring), and a
 * relative positioning context so popovers (AttachButton, Mode dropdown) can anchor
 * inside without a portal. Wraps in a `<form>` so the parent can attach a
 * single `onSubmit` listener — Cmd+Enter / Enter inside the textarea route
 * through `form.requestSubmit()` (see `Composer.Textarea`).
 */
export interface ComposerRootProps
  extends React.FormHTMLAttributes<HTMLFormElement> {}

export const ComposerRoot = React.forwardRef<HTMLFormElement, ComposerRootProps>(
  ({ className, children, ...rest }, ref) => (
    <form
      ref={ref}
      className={cn(
        // Layout — vertical stack with the chip bar / textarea / action bar
        "relative flex flex-col",
        // Soft card (Jul 12, 2026 UX) — a WARM hairline (``cream-deep``, NOT the
        // black-based ``hairline`` token that read as a cold outline) + soft
        // shadow so the card reads as part of the cream surface, not a boxed
        // control. Focus deepens the shadow (a gentle lift), no coloured ring.
        "rounded-[14px] border border-cream-deep bg-white/45",
        "shadow-[0_1px_3px_-1px_rgba(0,0,0,0.05),0_6px_20px_-10px_rgba(0,0,0,0.09)]",
        "transition-[box-shadow] duration-[280ms] ease-smooth",
        "focus-within:shadow-[0_2px_6px_-2px_rgba(0,0,0,0.07),0_12px_32px_-12px_rgba(0,0,0,0.13)]",
        className
      )}
      {...rest}
    >
      {children}
    </form>
  )
);
ComposerRoot.displayName = "Composer.Root";
